import styled from 'styled-components';
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { Form, useParams, useSearchParams } from 'react-router-dom';
import { APIEndPoints, Request } from '../../apis';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import type { TCourse } from '../../common/types/Course';
import {
  ButtonContainer,
  InputCheck,
  InputWrapper,
  Modal,
  PrimaryButton,
  ProcessedMessage,
  SecondaryButton,
  SectionHeading,
  SectionPreTitle,
  SectionTitle,
  SectionWrapper
} from '../atoms';
import { PRODUCT_TYPES } from '../../common/utils/constants';
import { useQuery } from '@tanstack/react-query';
import type { TProduct } from '../../common/types/Product';
import uniqBy from 'lodash/uniqBy';

type TNewCourseModal = {
  isModalOpen: boolean;
  handleModalClose: () => void;
  setUpdateSuccessful: React.Dispatch<React.SetStateAction<boolean>>;
};

const createFormSchema = z.object({
  courseCode: z.string(),
  courseName: z
    .string()
    .max(100, 'Course Name must be less than 100 characters'),
  courseAmount: z.preprocess((val) => Number(val), z.number()).optional(),
  nationallyAccredited: z.boolean(),
  paPackage: z.string().optional(),
  productId: z.string()
});

type TFormFields = {
  courseCode: string;
  courseName: string;
  courseAmount?: number;
  nationallyAccredited: boolean;
  paPackage?: string;
  productId: string;
};

type TOptions = {
  cost?: number;
  packagePolId?: string;
  merchantId?: string;
};

type InputWrapperProps = Array<{
  value: string;
  text: string | number;
}> | null;

type CreateFormSchema = z.infer<typeof createFormSchema>;

const FormFooter = styled((props) => <ButtonContainer {...props} />)`
  margin-top: 45px;
`;

const NewCourseModal = ({
  isModalOpen,
  handleModalClose,
  setUpdateSuccessful
}: TNewCourseModal) => {
  const [coursesList, setCoursesList] = useState<InputWrapperProps>(null);
  const [successfullyProcessed, setSuccessfullyProcessed] = useState<
    null | boolean
  >(null);
  const [searchParams] = useSearchParams();
  //#TODO rework with courses per merchant --------------------------
  const { merchantId } = useParams();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<CreateFormSchema>({
    resolver: zodResolver(createFormSchema)
  });

  const { isLoading, data: productList = [] } = useQuery({
    queryKey: [APIEndPoints.products, 'merchant', merchantId],
    cacheTime: 60 * 1000, // 1 minute
    queryFn: () => {
      return Request.get({
        path: `${APIEndPoints.products}/merchant/${merchantId}?access_token`,
        schema: z.any()
      });
    },
    enabled: !!merchantId,
    select: (data) => {
      return uniqBy(data || [], 'productType').map((product: TProduct) => ({
        value: product.id,
        text: product.productType
      }));
    }
  });

  useEffect(() => {
    const getCourses = async () => {
      const courses = await Request.safeGet({
        path: `${APIEndPoints.courses}/${merchantId}/productType/${PRODUCT_TYPES.PAYMENT_ASSIST}?access_token`,
        schema: z.any()
      });
      if (courses.type === 'right') {
        const optionaList = courses.value.map((course: TCourse) => ({
          value: course.id,
          text: course.polName
        }));
        setCoursesList(optionaList);
      }
    };

    getCourses();
  }, []);

  const editCourseSchema = z.object({
    id: z.string(),
    status: z.number(),
    message: z.string()
  });

  const onSubmit: SubmitHandler<CreateFormSchema> = async (
    data: TFormFields
  ) => {
    setUpdateSuccessful(false);

    const options: TOptions = {};
    if (data.paPackage && data.paPackage !== '') {
      options.packagePolId = data.paPackage;
    }
    if (data.courseAmount) {
      options.cost = Number(data.courseAmount);
    }
    if (merchantId) {
      options.merchantId = merchantId;
    }

    try {
      const createCourse = await Request.post(
        `${APIEndPoints.courses}/${merchantId}?access_token`,
        editCourseSchema,
        {
          nationallyAccredited: data.nationallyAccredited,
          polName: data.courseName,
          polCode: data.courseCode,
          productId: data.productId,
          ...options
        }
      );

      setSuccessfullyProcessed(true);
      setUpdateSuccessful(true);
    } catch {
      setSuccessfullyProcessed(false);
      setUpdateSuccessful(false);
    }
  };

  const handleReset = () => {
    setSuccessfullyProcessed(null);
    reset();
    handleModalClose();
  };

  return (
    <Modal
      handleClose={handleReset}
      isModalVisible={isModalOpen}
      content={
        <>
          <SectionPreTitle>Course</SectionPreTitle>
          <SectionTitle>Create New Course</SectionTitle>
          {successfullyProcessed === null && (
            <Form onSubmit={handleSubmit(onSubmit)}>
              <SectionWrapper>
                <SectionHeading>Course Details</SectionHeading>
                <InputWrapper
                  name="courseCode"
                  type="text"
                  label="Course Code"
                  required
                  register={register}
                  errorMessage={errors.courseCode?.message}
                />
                <InputWrapper
                  name="courseName"
                  type="text"
                  maxLength={100}
                  label="Course Name"
                  required
                  register={register}
                  errorMessage={errors.courseName?.message}
                />
                <InputWrapper
                  name="courseAmount"
                  type="currency"
                  label="Course Amount"
                  register={register}
                  errorMessage={errors.courseAmount?.message}
                />
                {!!productList?.length && (
                  <InputWrapper
                    name="productId"
                    type="select"
                    label="Product"
                    required
                    register={register}
                    complexOptions={productList}
                    errorMessage={errors.productId?.message}
                  />
                )}
                <InputCheck
                  name="nationallyAccredited"
                  label="Nationally Accredited"
                  register={register}
                  errorMessage={errors.nationallyAccredited?.message}
                />
              </SectionWrapper>
              {!!productList?.length && (
                <SectionWrapper>
                  <SectionHeading>Package</SectionHeading>
                  <InputWrapper
                    name="paPackage"
                    type="select"
                    label="Package PA Course"
                    complexOptions={productList}
                    register={register}
                    errorMessage={errors.paPackage?.message}
                  />
                </SectionWrapper>
              )}
              <SectionWrapper>
                <FormFooter
                  horizontalSpacing="10px"
                  verticalSpacing="0px"
                  justify="end"
                >
                  <SecondaryButton
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReset();
                    }}
                  >
                    Cancel
                  </SecondaryButton>
                  <PrimaryButton type="submit">Create Course</PrimaryButton>
                </FormFooter>
              </SectionWrapper>
            </Form>
          )}
          {successfullyProcessed === true && (
            <ProcessedMessage heading="Success">
              <p>Update was successfully made.</p>
            </ProcessedMessage>
          )}
          {successfullyProcessed === false && (
            <ProcessedMessage error={true} heading="Error">
              <p>Something went wrong when updating</p>
            </ProcessedMessage>
          )}
        </>
      }
    />
  );
};

export { NewCourseModal };
